const ANALYTICS_BASE_URL = "https://analytics.invalid";

const SENSITIVE_PARAMETER_NAMES = new Set([
  "token",
  "accesstoken",
  "refreshtoken",
  "activationtoken",
  "activationaccesstoken",
  "agentaccesstoken",
  "invitetoken",
  "invitationtoken",
  "granttoken",
  "sharetoken",
  "authtoken",
  "sessiontoken",
  "ssotoken",
  "tokenhash",
  "otp",
  "code",
  "authorizationcode",
  "apikey",
  "clientsecret",
  "secret",
  "password",
]);

const PRIVATE_ACCESS_PARAMETER_NAMES = new Set([
  "accesstoken",
  "activationaccesstoken",
  "agentaccesstoken",
]);

const compactParameterName = (name: string) =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

export const isSensitiveUrlParameter = (name: string): boolean =>
  SENSITIVE_PARAMETER_NAMES.has(compactParameterName(name));

export const isPrivateAccessTokenParameter = (name: string): boolean =>
  PRIVATE_ACCESS_PARAMETER_NAMES.has(compactParameterName(name));

const removeMatchingParameters = (
  params: URLSearchParams,
  predicate: (name: string) => boolean,
): boolean => {
  const namesToDelete = [...params.keys()].filter(predicate);
  namesToDelete.forEach((name) => params.delete(name));
  return namesToDelete.length > 0;
};

export const removeSensitiveUrlParameters = (
  params: URLSearchParams,
): boolean => removeMatchingParameters(params, isSensitiveUrlParameter);

export const removePrivateAccessTokenParameters = (
  params: URLSearchParams,
): boolean => removeMatchingParameters(params, isPrivateAccessTokenParameter);

interface SanitizedHash {
  hash: string;
  changed: boolean;
}

const sanitizeHashParameters = (
  hash: string,
  removeParameters: (params: URLSearchParams) => boolean,
): SanitizedHash => {
  if (!hash) return { hash, changed: false };

  const rawHash = hash.slice(1);
  const routeQueryIndex = rawHash.startsWith("/") ? rawHash.indexOf("?") : -1;
  if (routeQueryIndex >= 0) {
    const route = rawHash.slice(0, routeQueryIndex);
    const params = new URLSearchParams(rawHash.slice(routeQueryIndex + 1));
    const changed = removeParameters(params);
    if (!changed) return { hash, changed: false };
    const query = params.toString();
    return { hash: `#${route}${query ? `?${query}` : ""}`, changed: true };
  }

  // Ordinary anchors (for example, #pricing) are not query strings. OAuth and
  // private-access fragments use the form #key=value&key=value.
  if (!rawHash.includes("=")) return { hash, changed: false };

  const params = new URLSearchParams(rawHash);
  const changed = removeParameters(params);
  if (!changed) return { hash, changed: false };
  const value = params.toString();
  return { hash: value ? `#${value}` : "", changed: true };
};

export const sanitizeHashForAnalytics = (hash: string): string =>
  sanitizeHashParameters(hash, removeSensitiveUrlParameters).hash;

export const scrubPrivateAccessTokenFromHash = (hash: string): string =>
  sanitizeHashParameters(hash, removePrivateAccessTokenParameters).hash;

const isAbsoluteUrl = (value: string) => /^[a-z][a-z0-9+.-]*:/i.test(value);

/**
 * Removes bearer-like values from an analytics URL while retaining safe
 * attribution parameters. Handles both query strings and OAuth-style hashes.
 */
export const sanitizeUrlForAnalytics = (value: string): string => {
  if (!value) return value;

  try {
    const absolute = isAbsoluteUrl(value);
    const protocolRelative = value.startsWith("//");
    const url = new URL(value, ANALYTICS_BASE_URL);
    removeSensitiveUrlParameters(url.searchParams);
    url.hash = sanitizeHashForAnalytics(url.hash);

    if (absolute) return url.toString();
    if (protocolRelative) {
      return `//${url.host}${url.pathname}${url.search}${url.hash}`;
    }
    if (value.startsWith("?")) return `${url.search}${url.hash}`;
    if (value.startsWith("#")) return url.hash;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    // Browser page locations and referrers are valid URLs. If a future caller
    // supplies an invalid value, avoid transforming it into a misleading URL.
    return value;
  }
};
